// Parent Corner — PIN-gated readiness dashboard (EDUCATION-DESIGN.md §5.5).
// Entirely local: no account, no upload, nothing leaves the device.

import type { GameStateStore } from '../core/store';
import { t } from '../i18n';
import type { SupabaseClient } from '../net/supabase';
import { getItem } from '../sim/items';
import { readinessMetrics, TRIAL_DAYS } from '../sim/progression';
import { openAccountPanel } from './accountPanel';
import { el, escapeHtml, modal } from './dom';

/**
 * The PIN keeps a curious kid out of the parent view. It is a gate, not a
 * secret — nothing behind it is sensitive, so a plain digest is the right
 * weight of protection here.
 *
 * True only because the digest never leaves the device: `toSyncPayload`
 * (net/cloudSync.ts) keeps it out of the cloud save. A 4-digit PIN is 10,000
 * candidates, which is fine in front of a child and instant for anyone holding
 * a copy of the save.
 */
export async function hashPin(pin: string): Promise<string> {
  const bytes = new TextEncoder().encode(`clayfarm:${pin}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * R4: 5-try cooldown. The PIN is a gate, not a vault — but a determined
 * 11-year-old can brute-force 10k combos, so slow them down.
 */
const PIN_MAX_TRIES = 5;
const PIN_LOCK_MS = 60_000;

/**
 * The cooldown state, exported because there are two Parent Corner screens and
 * only the one that was reachable had this wired in. `openParentSheet`
 * (app/screens/settings.ts) now calls the same three functions as
 * `openParentCorner`, so the lockout is where the comment always said it was.
 */
export function pinGate(): { count: number; lockedUntil: number } {
  try {
    return {
      count: Number(window.localStorage.getItem('cf_pin_tries') ?? 0),
      lockedUntil: Number(window.localStorage.getItem('cf_pin_lock') ?? 0),
    };
  } catch {
    return { count: 0, lockedUntil: 0 };
  }
}

export function recordPinMiss(): number {
  const count = pinGate().count + 1;
  try {
    window.localStorage.setItem('cf_pin_tries', String(count));
    if (count >= PIN_MAX_TRIES) {
      window.localStorage.setItem('cf_pin_lock', String(Date.now() + PIN_LOCK_MS));
    }
  } catch {
    /* private mode: no persistence, no lockout */
  }
  return count;
}

export function clearPinTries(): void {
  try {
    window.localStorage.removeItem('cf_pin_tries');
    window.localStorage.removeItem('cf_pin_lock');
  } catch {
    /* ignore */
  }
}

export function openParentCorner(
  host: HTMLElement,
  store: GameStateStore,
  client?: SupabaseClient,
  onStatus?: (message: string) => void,
): void {
  const { body } = modal(host, t('parent.title'), { wide: true });
  const existing = store.getState().settings.parentPinHash;

  const showGate = (): void => {
    body.replaceChildren();
    body.appendChild(
      el('p', 'mb-2 text-sm', escapeHtml(existing ? t('parent.pinPrompt') : t('parent.pinSet'))),
    );
    const input = el('input', 'cf-chip w-32 px-3 py-2 text-lg') as HTMLInputElement;
    input.type = 'password';
    input.inputMode = 'numeric';
    input.maxLength = 4;
    input.setAttribute('aria-label', t('parent.pinPrompt'));

    const error = el('p', 'mt-2 hidden text-xs text-rose-300', escapeHtml(t('parent.pinWrong')));
    const submit = el('button', 'cf-btn ml-2 px-4', escapeHtml(t('parent.unlock')));
    const gate = pinGate();
    if (gate.lockedUntil > Date.now()) {
      error.textContent = t('parent.pinLocked');
      error.classList.remove('hidden');
      submit.disabled = true;
    }
    submit.addEventListener('click', () => {
      void (async () => {
        if (pinGate().lockedUntil > Date.now()) {
          error.textContent = t('parent.pinLocked');
          error.classList.remove('hidden');
          return;
        }
        const hash = await hashPin(input.value);
        if (!existing) {
          store.mutate((s) => {
            s.settings.parentPinHash = hash;
          });
          clearPinTries();
          showDashboard();
          return;
        }
        if (hash === existing) {
          clearPinTries();
          showDashboard();
        } else {
          recordPinMiss();
          error.classList.remove('hidden');
        }
      })();
    });

    const row = el('div', 'flex items-center');
    row.append(input, submit);
    body.append(row, error);
    input.focus();
  };

  const showDashboard = (): void => {
    const state = store.getState();
    const metrics = readinessMetrics(state);
    body.replaceChildren();

    const stats: Array<[string, string]> = [
      [t('parent.streak'), `${metrics.careStreak} ${t('common.days')}`],
      [t('parent.promptness'), `${metrics.promptnessPercent}%`],
      [t('parent.unassisted'), `${metrics.unassistedPercent}%`],
      [t('parent.incidents'), String(metrics.incidents)],
      [t('parent.trialWindow'), `${metrics.daysTracked}/${TRIAL_DAYS} ${t('common.days')}`],
    ];
    const grid = el('div', 'grid gap-2 sm:grid-cols-2');
    for (const [label, value] of stats) {
      const card = el('div', 'rounded-xl bg-black/20 p-3');
      card.innerHTML = `<div class="text-xs opacity-70">${escapeHtml(label)}</div>
        <div class="text-lg font-bold tabular-nums">${escapeHtml(value)}</div>`;
      grid.appendChild(card);
    }
    body.appendChild(grid);

    const verdict = el(
      'div',
      `mt-3 rounded-xl p-3 text-sm ${metrics.ready ? 'bg-emerald-700/40' : 'bg-black/20'}`,
      escapeHtml(
        metrics.ready
          ? t('parent.readyYes')
          : t('parent.readyNo', { days: metrics.daysRemaining }),
      ),
    );
    body.appendChild(verdict);

    // Real-cost summary from the ECONOMY.md tags — the honest sticker price.
    // R4: footnoted feed+bedding-only so nobody is surprised by oyster/grit/vet.
    body.appendChild(
      el('p', 'mt-3 text-sm', escapeHtml(t('parent.monthlyCost', { cost: monthlyRealCost(store) }))),
    );
    body.appendChild(
      el('p', 'text-xs opacity-70', escapeHtml(t('parent.costNote'))),
    );

    // R4: delete + sign-in live in Family Account; parents look here first,
    // so bridge them over instead of stranding them.
    const account = el('button', 'cf-btn mt-3 px-4 text-xs', escapeHtml(t('parent.accountButton')));
    account.addEventListener('click', () => {
      if (client && onStatus) openAccountPanel(host, store, client, onStatus);
      else body.appendChild(el('p', 'mt-2 text-xs opacity-70', escapeHtml(t('parent.accountOffline'))));
    });
    body.appendChild(account);

    if (metrics.ready) {
      const print = el('button', 'cf-btn mt-3 px-4 text-xs', escapeHtml(t('parent.printReadiness')));
      print.addEventListener('click', () => window.print());
      body.appendChild(print);
    }
  };

  showGate();
}

/**
 * Monthly running cost: feed and bedding are the recurring items, so the
 * estimate is the real-world price of what this flock actually consumes.
 */
export function monthlyRealCost(store: GameStateStore): number {
  const state = store.getState();
  const birds = Math.max(1, state.flock.length);
  // A standard hen eats roughly a quarter pound of feed a day; a 50 lb bag
  // therefore lasts one bird about 200 days.
  const feedBag = getItem('feed_layer')?.realCostUsd ?? 20;
  const bedding = getItem('wood_shavings')?.realCostUsd ?? 8;
  const feedPerMonth = (birds * 30) / 200;
  return Math.round(feedPerMonth * feedBag + bedding);
}
